import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, Table, ProgressBar, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { fetchAnalyticsTopProducts } from '../../services/api';
import styles from './TopProdutosVistos.module.css';

// Função para buscar imagem do produto pelo código ou nome
const getProductImageUrl = (product) => {
    // Se já tem imagem, retorna
    if (product.imagem) return product.imagem;

    // Tentar construir URL da imagem pelo código do produto
    if (product.id) {
        // URL padrão do e-commerce Gringão
        return `https://gfrfrv.vtexassets.com/arquivos/ids/${product.id}-300-auto?v=638309247082570000`;
    }

    return null;
};

const TopProdutosVistos = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [dias, setDias] = useState(30);
    const [productImages, setProductImages] = useState({});
    const [hoveredProduct, setHoveredProduct] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchAnalyticsTopProducts(dias, 50);
            if (result.success) {
                setData(result);
            } else {
                setError(result.error || 'Erro ao carregar dados');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dias]);

    const formatNumber = (num) => {
        if (!num) return '0';
        return new Intl.NumberFormat('pt-BR').format(num);
    };

    // Gerar estratégia baseada nos dados do produto
    const getProductStrategy = (product) => {
        const views = product.views || 0;
        const cart = product.addedToCart || 0;
        const purchased = product.purchased || 0;
        const conversionRate = product.conversionRate || 0;

        // Calcular taxas
        const cartRate = views > 0 ? (cart / views) * 100 : 0;
        const purchaseFromCart = cart > 0 ? (purchased / cart) * 100 : 0;

        let icon = '';
        let status = '';
        let strategy = '';
        let priority = '';

        if (conversionRate >= 5) {
            // Produto campeão
            icon = '🏆';
            status = 'Campeão de Vendas';
            strategy = 'Destaque na vitrine principal. Considere aumentar estoque.';
            priority = 'success';
        } else if (cartRate >= 10 && purchaseFromCart < 30) {
            // Alto interesse, problema no checkout
            icon = '🚨';
            status = 'Abandono de Carrinho';
            strategy = 'Revisar preço de frete ou oferecer cupom de desconto.';
            priority = 'danger';
        } else if (cartRate < 5 && views > 50) {
            // Muitas views, pouco interesse
            icon = '📸';
            status = 'Baixo Engajamento';
            strategy = 'Melhorar fotos e descrição. Revisar preço competitivo.';
            priority = 'warning';
        } else if (cartRate >= 5 && conversionRate >= 2) {
            // Bom desempenho
            icon = '✅';
            status = 'Performance Boa';
            strategy = 'Manter exposição. Incluir em campanhas de remarketing.';
            priority = 'info';
        } else if (views < 30) {
            // Pouca visibilidade
            icon = '📢';
            status = 'Baixa Visibilidade';
            strategy = 'Investir em tráfego pago ou destaque no site.';
            priority = 'secondary';
        } else {
            // Análise geral
            icon = '📊';
            status = 'Em Análise';
            strategy = 'Monitorar por mais tempo para definir estratégia.';
            priority = 'secondary';
        }

        return { icon, status, strategy, priority, cartRate: cartRate.toFixed(1), purchaseFromCart: purchaseFromCart.toFixed(1) };
    };

    // Renderizar tooltip com estratégia (sem imagem)
    const renderProductTooltip = (product) => {
        const analysis = getProductStrategy(product);

        return (
            <Tooltip id={`tooltip-${product.id || product.name}`} className={styles.strategyTooltip}>
                <div className={styles.tooltipContent}>
                    <div className={styles.tooltipInfo}>
                        <strong>{product.name}</strong>
                        {product.id && <span className={styles.tooltipCode}>{product.id}</span>}
                    </div>
                    <div className={`${styles.tooltipStrategy} ${styles[`strategy${analysis.priority}`]}`}>
                        <div className={styles.strategyHeader}>
                            <span className={styles.strategyIcon}>{analysis.icon}</span>
                            <span className={styles.strategyStatus}>{analysis.status}</span>
                        </div>
                        <p className={styles.strategyText}>{analysis.strategy}</p>
                        <div className={styles.strategyMetrics}>
                            <span>📊 Taxa p/ carrinho: {analysis.cartRate}%</span>
                            <span>💰 Carrinho → Compra: {analysis.purchaseFromCart}%</span>
                        </div>
                    </div>
                </div>
            </Tooltip>
        );
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Carregando dados do Google Analytics...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="warning" className="m-4">
                <Alert.Heading>⚠️ Não foi possível carregar os dados</Alert.Heading>
                <p>{error}</p>
                <hr />
                <p className="mb-0 text-muted">
                    Verifique se o Google Analytics 4 está configurado corretamente.
                </p>
            </Alert>
        );
    }

    const products = data?.data || [];
    const maxViews = products.length > 0 ? products[0].views : 1;

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerInfo}>
                    <h4 className={styles.title}>🔥 Produtos Mais Visualizados</h4>
                    <p className={styles.subtitle}>
                        Dados do Google Analytics 4 • Fonte: {data?.source === 'enhanced_ecommerce' ? 'Enhanced E-commerce' : 'Page Views'}
                    </p>
                </div>
                <div className={styles.headerControls}>
                    <Form.Select
                        value={dias}
                        onChange={(e) => setDias(parseInt(e.target.value))}
                        className={styles.periodSelect}
                    >
                        <option value={7}>Últimos 7 dias</option>
                        <option value={14}>Últimos 14 dias</option>
                        <option value={30}>Últimos 30 dias</option>
                        <option value={60}>Últimos 60 dias</option>
                        <option value={90}>Últimos 90 dias</option>
                    </Form.Select>
                </div>
            </div>

            {/* Stats Cards */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className={styles.statCard}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>👁️</div>
                            <div className={styles.statValue}>{formatNumber(data?.totalViews)}</div>
                            <div className={styles.statLabel}>Visualizações Totais</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className={styles.statCard}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>📦</div>
                            <div className={styles.statValue}>{formatNumber(data?.totalProducts)}</div>
                            <div className={styles.statLabel}>Produtos Únicos</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className={styles.statCard}>
                        <Card.Body className="text-center">
                            <div className={styles.statIcon}>📊</div>
                            <div className={styles.statValue}>
                                {products.length > 0 ? formatNumber(Math.round(data?.totalViews / products.length)) : '0'}
                            </div>
                            <div className={styles.statLabel}>Média por Produto</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Products Table */}
            {products.length === 0 ? (
                <Alert variant="info">
                    <div className="text-center py-4">
                        <div style={{ fontSize: '3rem' }}>📊</div>
                        <h5 className="mt-3">Nenhum dado de visualização encontrado</h5>
                        <p className="text-muted mb-0">
                            Verifique se o site está configurado com Google Analytics 4 e Enhanced E-commerce.
                        </p>
                    </div>
                </Alert>
            ) : (
                <Card className={styles.tableCard}>
                    <Card.Body className="p-0">
                        <Table hover responsive className={styles.productsTable}>
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th>Produto</th>
                                    {data?.source === 'enhanced_ecommerce' && (
                                        <>
                                            <th style={{ width: '100px' }}>Carrinho</th>
                                            <th style={{ width: '100px' }}>Compras</th>
                                            <th style={{ width: '80px' }}>Conversão</th>
                                        </>
                                    )}
                                    <th style={{ width: '120px' }}>Visualizações</th>
                                    <th style={{ width: '200px' }}>Popularidade</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((product, index) => {
                                    const percentage = Math.round((product.views / maxViews) * 100);
                                    const isTop3 = index < 3;
                                    const medals = ['🥇', '🥈', '🥉'];

                                    return (
                                        <OverlayTrigger
                                            key={product.id || index}
                                            placement="right"
                                            delay={{ show: 300, hide: 100 }}
                                            overlay={renderProductTooltip(product)}
                                        >
                                            <tr className={`${isTop3 ? styles.topRow : ''} ${styles.productRow}`}>
                                                <td className="text-center">
                                                    {isTop3 ? (
                                                        <span style={{ fontSize: '1.2rem' }}>{medals[index]}</span>
                                                    ) : (
                                                        <span className="text-muted">{index + 1}</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className={styles.productInfo}>
                                                        <strong className={styles.productName}>{product.name}</strong>
                                                        {product.id && (
                                                            <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.7rem' }}>
                                                                {product.id}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </td>
                                                {data?.source === 'enhanced_ecommerce' && (
                                                    <>
                                                        <td className="text-center">
                                                            <Badge bg="warning" text="dark">
                                                                🛒 {formatNumber(product.addedToCart)}
                                                            </Badge>
                                                            {product.dataSuspicious && (
                                                                <OverlayTrigger
                                                                    placement="top"
                                                                    overlay={
                                                                        <Tooltip id={`suspicious-${product.id}`}>
                                                                            ⚠️ Dados inconsistentes: {product.suspiciousReason || 'Verifique o rastreamento do GA4'}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <span style={{
                                                                        marginLeft: '5px',
                                                                        cursor: 'help',
                                                                        color: '#dc3545',
                                                                        fontSize: '1rem'
                                                                    }}>⚠️</span>
                                                                </OverlayTrigger>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="success">
                                                                ✅ {formatNumber(product.purchased)}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg={product.conversionRate > 5 ? 'success' : product.conversionRate > 2 ? 'warning' : 'secondary'}>
                                                                {product.conversionRate}%
                                                            </Badge>
                                                        </td>
                                                    </>
                                                )}
                                                <td className="text-center">
                                                    <strong>{formatNumber(product.views)}</strong>
                                                </td>
                                                <td>
                                                    <ProgressBar
                                                        now={percentage}
                                                        variant={percentage > 70 ? 'danger' : percentage > 40 ? 'warning' : 'info'}
                                                        className={styles.popularityBar}
                                                    />
                                                </td>
                                            </tr>
                                        </OverlayTrigger>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )
            }

            {/* Footer Info */}
            <div className={styles.footerInfo}>
                <small className="text-muted">
                    📅 Período: {data?.period?.startDate} até {data?.period?.endDate} •
                    🔄 Última atualização: {new Date().toLocaleString('pt-BR')}
                    {products.filter(p => p.dataSuspicious).length > 0 && (
                        <span style={{ color: '#dc3545', marginLeft: '10px' }}>
                            • ⚠️ {products.filter(p => p.dataSuspicious).length} produto(s) com dados inconsistentes
                        </span>
                    )}
                </small>
            </div>
        </div >
    );
};

export default TopProdutosVistos;

